/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/**
 * Presentation-only derivations for every surface that shows scheduling state: the Schedule_Modal, the
 * workspace card in both renderings, and the workspace detail page.
 *
 * Kept separate from `schedule-tag-format.js` (Requirement 16.11) so that the grammar module stays
 * free of display concerns and this module stays free of grammar concerns: nothing here builds or
 * validates a tag value. It turns an already-parsed schedule model, plus the deployment settings
 * delivered in the SchedulingInfo response, into strings and option lists. The one exception is the
 * pair of card phrases at the foot of the file, which are handed a raw mirrored tag value and call
 * `parseScheduleValue` rather than picking it apart, so the grammar stays where it belongs.
 *
 * This module is the one place that knows a stop time earlier than its start time means the window
 * closes on the following day (Requirement 6.4), and the one place that renders a timestamp in the
 * deployment timezone rather than the browser's (Requirements 5.6 and 7.7).
 */

import {
  parseScheduleValue,
  DAY_ORDER,
  DAY_KIND_OFF,
  DAY_KIND_RANGE,
  MODE_NONE,
  MODE_GROUPS,
  MODE_CUSTOM,
} from './schedule-tag-format';

const MINUTES_IN_DAY = 24 * 60;

// Falls back to the External_Scheduler's own default interval rather than to a minute-by-minute list,
// because a dropdown of 1440 options is worse than a wrong-but-usable one, and the real value always
// arrives in the SchedulingInfo response.
const DEFAULT_INTERVAL_MINUTES = 30;

// The idle timeouts offered, before the deployment floor is applied. Coarse on purpose: the choice is
// "how long am I prepared to pay for an idle workspace", which nobody answers to the minute. 45 was
// dropped for that reason: it sat between two rungs nobody was choosing between.
//
// A value already set on a workspace but no longer on this ladder is not lost. The idle section adds
// it back as its own option, labelled as the current value, so removing a rung never silently
// rewrites what a workspace is configured with.
//
// The days rungs, 1 to 7 days, are for workspaces left doing low-CPU work for long stretches. The list
// is shown in Hours and Days groups (`idleStopGroup`) so its length stays easy to scan.
const IDLE_STOP_LADDER = [
  30,
  60,
  90,
  120,
  180,
  240,
  360,
  480,
  720,
  ...[1, 2, 3, 4, 5, 6, 7].map(days => days * MINUTES_IN_DAY),
];

const IDLE_STOP_GROUP_HOURS = 'Hours';
const IDLE_STOP_GROUP_DAYS = 'Days';

/**
 * Which group of the idle timeout list a number of minutes belongs in: Days from 24 hours up,
 * otherwise Hours (which also holds the sub-hour rungs).
 *
 * @param {number} minutes
 * @returns {string}
 */
function idleStopGroup(minutes) {
  return minutes >= MINUTES_IN_DAY ? IDLE_STOP_GROUP_DAYS : IDLE_STOP_GROUP_HOURS;
}

const DAY_LABELS = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
  SUN: 'Sunday',
};

const SHORT_DAY_LABELS = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun',
};

const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
const WEEKEND = ['SAT', 'SUN'];

// Written once so that the summary, the group labels and the card phrase all say it the same way. A
// day covered from midnight to midnight is described as all day, never as a zero-length or overnight
// window.
const ALL_DAY_PHRASE = 'all day';

// The wording for a workspace with nothing set. Also the sentinel the card phrase tests against to
// decide whether to render a marker at all.
const NO_SCHEDULE_PHRASE = 'no schedule';

// Shown wherever a locked idle timeout is rendered: beside the read-only value in the scheduling modal
// and on the workspace detail page. Written once so both say it identically. They previously carried
// their own near-identical wording and drifted apart, which is the failure this constant prevents.
// The backend's own rejection message names the same fact and should be reworded with it.
const IDLE_STOP_LOCKED_NOTICE = 'This configuration is managed by your admin and cannot be changed.';

// Separates a preset's name from its description in the hover text. The middot is the separator this
// codebase already uses to join two independent facts on one line.
const LABEL_SEPARATOR = ' · ';

/**
 * Upper-cases the first character of a phrase and leaves the rest alone.
 */
function upperFirst(text) {
  if (typeof text !== 'string' || text.length === 0) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A preset's name as shown to a user on its own or at the start of a line: `day` becomes `Day`.
 *
 * Group names are stored lower case in the groups table because they are tag tokens, and the tag keeps
 * them that way. Only the first letter is raised, so `always-on` reads `Always-on` and an
 * administrator's own capitals, such as `GPU`, are left alone. Use the raw name wherever it is the tag
 * value or sits mid-sentence.
 *
 * @param {string} name
 * @returns {string}
 */
function displayGroupName(name) {
  return typeof name === 'string' ? upperFirst(name.trim()) : '';
}

/**
 * Renders minutes from midnight as `HH:MM`. 1440 renders as `24:00` so that a midnight-to-midnight
 * group reads as a full day rather than as a zero-length window.
 */
function minutesToTime(minutes) {
  if (!Number.isFinite(minutes)) return '';

  const clamped = Math.max(0, Math.min(MINUTES_IN_DAY, Math.round(minutes)));
  const hour = Math.floor(clamped / 60);
  const minute = clamped % 60;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Renders a group's day array as a phrase, collapsing the two combinations users recognise.
 */
function formatDayList(days) {
  const ordered = DAY_ORDER.filter(day => (days || []).some(entry => String(entry).toUpperCase() === day));

  if (ordered.length === 0) return 'No days';
  if (ordered.length === DAY_ORDER.length) return 'Every day';
  if (ordered.join() === WEEKDAYS.join()) return 'Weekdays';
  if (ordered.join() === WEEKEND.join()) return 'Weekends';

  return ordered.map(day => SHORT_DAY_LABELS[day]).join(', ');
}

/**
 * Renders a start and stop time as a window phrase.
 *
 * A stop time at or before the start time is an overnight window that closes the following day, which
 * is the scheduler's behaviour and the single most surprising thing about it (Requirement 6.4). Equal
 * start and stop times cannot occur in a value the backend accepted, so they are treated as overnight
 * rather than being given their own wording.
 */
function formatWindow(start, stop) {
  if (start === stop) return `${start} to ${stop} the next day`;

  const overnight = start > stop;
  return overnight ? `${start} to ${stop} the next day` : `${start} to ${stop}`;
}

/**
 * Renders a group's hours as a phrase, from the group's own minute fields.
 *
 * A group covering midnight to midnight is described as all day rather than as a zero-length or
 * overnight window. Shared with the group list component so that the all-day and overnight rules are
 * stated once.
 *
 * @param {number} startMinutes - Minutes from midnight
 * @param {number} stopMinutes - Minutes from midnight
 * @returns {string}
 */
function formatGroupWindow(startMinutes, stopMinutes) {
  if (isAllDayWindow(startMinutes, stopMinutes)) return ALL_DAY_PHRASE;

  return formatWindow(minutesToTime(startMinutes), minutesToTime(stopMinutes));
}

/**
 * True when a pair of minute fields covers midnight to midnight.
 *
 * The all-day rule stated once, so that the wording and the ordering cannot disagree about which
 * presets are the all-day ones.
 */
function isAllDayWindow(startMinutes, stopMinutes) {
  return startMinutes === 0 && stopMinutes >= MINUTES_IN_DAY;
}

/**
 * Renders hours then days as one phrase, `17:00 to 01:00 the next day, Weekdays`. The time leads
 * because it is what tells two presets on the same days apart.
 */
function formatTimesThenDays(windowPhrase, dayPhrase) {
  return `${upperFirst(windowPhrase)}, ${dayPhrase}`;
}

/**
 * Renders a group's hours and days as one phrase, or the empty string when the row does not carry
 * enough to describe a window.
 *
 * A row with no recognisable day, or without both minute fields, cannot be described as a schedule.
 * The empty string is returned rather than a half-written phrase, so that callers can fall back
 * rather than show ` to , No days`.
 */
function formatGroupSchedule(group) {
  const days = (group || {}).days;
  const named = Array.isArray(days) && DAY_ORDER.some(day => days.some(entry => String(entry).toUpperCase() === day));
  const startMinutes = (group || {}).startMinutes;
  const stopMinutes = (group || {}).stopMinutes;

  if (!named) return '';
  if (!Number.isFinite(startMinutes) || !Number.isFinite(stopMinutes)) return '';

  return formatTimesThenDays(formatGroupWindow(startMinutes, stopMinutes), formatDayList(days));
}

/**
 * The visible label for one preset option: the hours and days it runs, such as
 * `17:00 to 01:00 the next day, Weekdays`.
 *
 * Built from the row's `days` and hour columns, which are what the External_Scheduler acts on, rather
 * than from the free-text `description`, which can drift from the hours and reads unevenly row to row.
 * The preset's name is left out: the schedule is what the user is choosing, and the name and the
 * description are on the option's hover text instead.
 *
 * A row that cannot describe a window falls back to its name.
 *
 * @param {{ name: string, description: string, days: string[], startMinutes: number, stopMinutes: number }} group
 * @returns {string}
 */
function presetOptionLabel(group) {
  if (!group) return '';
  return formatGroupSchedule(group) || displayGroupName(group.name);
}

/**
 * The hover text for one preset option: its name, and the administrator's description when there
 * is one, such as `Day · Weekday business hours`.
 *
 * The name is what a support conversation and the tag value use, and the description is the only
 * place an administrator's own phrasing appears now that the label is derived.
 *
 * @param {{ name: string, description: string }} group
 * @returns {string}
 */
function presetOptionTitle(group) {
  const name = displayGroupName((group || {}).name);
  const described = typeof (group || {}).description === 'string' ? group.description.trim() : '';

  if (name.length === 0) return described;
  if (described.length === 0) return name;

  return `${name}${LABEL_SEPARATOR}${described}`;
}

/**
 * Orders the presets for display, from the rows themselves.
 *
 * A DynamoDB scan returns the groups table in whatever order it likes, which on screen reads as
 * random. The order is derived entirely from each row's own minute fields and name: no name is
 * special-cased and no list of expected names exists anywhere here, which is what keeps the option
 * list a function of the response (Property 12).
 *
 * All-day presets come last. An all-day window starts at minute zero, so ordering purely by start
 * time would put them at the top, where they read as arbitrary rather than as the special case they
 * are. Everything else ascends by start time, so the list reads through the day, then by stop time
 * and then by name so that the order is total and does not depend on what the scan happened to
 * return. Rows without usable minutes sort after the rows that have them, for the same reason.
 *
 * A copy is sorted, never the caller's array: this is asked for during a render, and mutating the
 * props would be a side effect of describing them.
 *
 * @param {Array<{ name: string, startMinutes: number, stopMinutes: number }>} groups
 * @returns {Array} A new array; the input is left as it was
 */
function sortPresetGroups(groups) {
  if (!Array.isArray(groups)) return [];

  // Non-finite minutes sort as beyond the end of the day, so an unusable row lands after the ones
  // that can be placed rather than at an arbitrary point among them.
  const minuteKey = value => (Number.isFinite(value) ? value : MINUTES_IN_DAY + 1);
  const allDayKey = group => (isAllDayWindow((group || {}).startMinutes, (group || {}).stopMinutes) ? 1 : 0);
  const nameKey = group => (typeof (group || {}).name === 'string' ? group.name.trim().toLowerCase() : '');

  return [...groups].sort((a, b) => {
    const allDay = allDayKey(a) - allDayKey(b);
    if (allDay !== 0) return allDay;

    const start = minuteKey((a || {}).startMinutes) - minuteKey((b || {}).startMinutes);
    if (start !== 0) return start;

    const stop = minuteKey((a || {}).stopMinutes) - minuteKey((b || {}).stopMinutes);
    if (stop !== 0) return stop;

    const nameA = nameKey(a);
    const nameB = nameKey(b);
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;

    // Two rows the account cannot tell apart keep the order they arrived in, which `Array#sort` is
    // required to preserve.
    return 0;
  });
}

/**
 * Finds a group by name, case-insensitively, because a name read back from the tag may differ in case
 * from the row in the groups table.
 */
function findGroup(name, groups) {
  const wanted = String(name)
    .trim()
    .toLowerCase();

  return (groups || []).find(
    entry =>
      entry &&
      String(entry.name)
        .trim()
        .toLowerCase() === wanted,
  );
}

/**
 * Produces the window list shown before the user confirms (Requirement 6.6), and on the detail page.
 *
 * Each line is `{ label, text }`. A custom timetable is one line per day, labelled with the day, so the
 * summary can show the days down one column and the hours down the next. A preset line has no label:
 * its text is the same phrase the preset dropdown shows, hours then days. Lines are returned as parts
 * rather than as `label: text` strings because a preset's text starts with a time, and splitting such a
 * string on its first colon would cut the time in half.
 *
 * @param {{ mode: string, selectedGroups: string[], days: Object }} model - As returned by
 *        `parseScheduleValue` or held by the modal
 * @param {Array<{ name: string, description: string, days: string[], startMinutes: number, stopMinutes: number }>} groups
 *        The groups from the SchedulingInfo response; never a hardcoded list (Requirement 5.2)
 * @returns {Array<{ label: string|null, text: string }>} One line per window, or an empty array when there
 *          is no timetable
 */
function summariseWindows(model, groups) {
  if (!model) return [];

  if (model.mode === MODE_GROUPS) {
    return (model.selectedGroups || []).map(name => {
      const group = findGroup(name, groups);

      if (!group) {
        return { label: null, text: `${displayGroupName(name)}: this group is not defined in this account` };
      }

      return { label: null, text: presetOptionLabel(group) };
    });
  }

  if (model.mode === MODE_CUSTOM) {
    const days = model.days || {};

    return DAY_ORDER.filter(day => days[day] && days[day].kind !== DAY_KIND_OFF).map(day => {
      const entry = days[day];
      const text = entry.kind === DAY_KIND_RANGE ? formatWindow(entry.start, entry.stop) : ALL_DAY_PHRASE;
      return { label: DAY_LABELS[day], text };
    });
  }

  return [];
}

/**
 * A summary line as one string, `Monday: 09:00 to 17:00`, for places that show lines as plain text.
 *
 * @param {{ label: string|null, text: string }} line
 * @returns {string}
 */
function formatSummaryLine(line) {
  if (!line) return '';
  return line.label ? `${line.label}: ${line.text}` : line.text;
}

/**
 * Renders a saved schedule as one short phrase, for the workspace card and the list.
 *
 * Deliberately coarser than `summariseWindows`: this is the one-line answer to "what is set", so a
 * week of differing hours collapses to the days plus a note that the hours differ rather than
 * spilling seven windows into a tooltip. A schedule expressing no window at all reads the same as
 * having none, because to the scheduler it is the same thing.
 */
function describeSavedSchedule(model, groups) {
  if (!model || model.mode === MODE_NONE) return NO_SCHEDULE_PHRASE;

  if (model.mode === MODE_GROUPS) {
    const names = (model.selectedGroups || []).filter(name => typeof name === 'string' && name.trim().length > 0);
    if (names.length === 0) return NO_SCHEDULE_PHRASE;

    // With the groups table, each preset reads as the dropdown labels it. Those labels contain commas
    // of their own, so they are separated with semicolons. Without the table (the status mirror),
    // presets can only be named, each as a standalone name.
    const resolved = names.map(name => findGroup(name, groups));
    if (resolved.some(Boolean)) {
      return names.map((name, index) => presetOptionLabel(resolved[index]) || displayGroupName(name)).join('; ');
    }
    return names.map(displayGroupName).join(', ');
  }

  const days = model.days || {};
  const scheduled = DAY_ORDER.filter(day => days[day] && days[day].kind !== DAY_KIND_OFF);
  if (scheduled.length === 0) return NO_SCHEDULE_PHRASE;

  const windows = scheduled.map(day =>
    days[day].kind === DAY_KIND_RANGE ? formatWindow(days[day].start, days[day].stop) : ALL_DAY_PHRASE,
  );
  const dayPhrase = formatDayList(scheduled);

  // Hours then days, the same order the preset labels use.
  return windows.every(window => window === windows[0])
    ? formatTimesThenDays(windows[0], dayPhrase)
    : `${dayPhrase}, hours vary by day`;
}

/**
 * The times the modal offers, at multiples of the scheduler's own invocation interval
 * (Requirement 5.4). Offering a finer grain than the scheduler runs at would promise a precision it
 * cannot deliver.
 *
 * @param {number} intervalMinutes - `schedulerIntervalMinutes` from the SchedulingInfo response
 * @returns {Array<{ key: string, value: string, text: string }>}
 */
function timeOptions(intervalMinutes) {
  const interval =
    Number.isFinite(intervalMinutes) && intervalMinutes > 0 && intervalMinutes <= MINUTES_IN_DAY
      ? Math.round(intervalMinutes)
      : DEFAULT_INTERVAL_MINUTES;

  const options = [];
  for (let minutes = 0; minutes < MINUTES_IN_DAY; minutes += interval) {
    const time = minutesToTime(minutes);
    options.push({ key: time, value: time, text: time });
  }

  return options;
}

/**
 * Renders a whole number of minutes as a duration phrase. Whole days read as days, so 1440 is
 * `1 day` rather than `24 hours`; anything else is hours and minutes.
 */
function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} minutes`;

  if (minutes >= MINUTES_IN_DAY && minutes % MINUTES_IN_DAY === 0) {
    const days = minutes / MINUTES_IN_DAY;
    return days === 1 ? '1 day' : `${days} days`;
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  const hourPhrase = hours === 1 ? '1 hour' : `${hours} hours`;

  return remainder === 0 ? hourPhrase : `${hourPhrase} ${remainder} minutes`;
}

/**
 * The idle timeouts the modal offers. Never returns a value below `minMinutes` (Requirement 7.2), and
 * includes the floor itself even when it is not one of the ladder's values, so that the smallest
 * permitted timeout is always selectable.
 *
 * The off option is not included here: removing the timeout deletes the tag, which is a different
 * operation from choosing a value, and the section that renders the control owns that distinction.
 *
 * Each option carries the `group` it is listed under, Hours or Days.
 *
 * @param {number} minMinutes - `schedulerIdleMinMinutes` from the SchedulingInfo response
 * @returns {Array<{ key: number, value: number, text: string, group: string }>}
 */
function idleStopOptions(minMinutes) {
  const floor = Number.isFinite(minMinutes) && minMinutes > 0 ? Math.ceil(minMinutes) : 0;

  const values = IDLE_STOP_LADDER.filter(minutes => minutes >= floor);
  if (floor > 0 && !values.includes(floor)) values.unshift(floor);

  return values
    .sort((a, b) => a - b)
    .map(minutes => ({
      key: minutes,
      value: minutes,
      text: formatDuration(minutes),
      group: idleStopGroup(minutes),
    }));
}

/**
 * Renders the projected stop time in the deployment timezone (Requirement 7.7).
 *
 * The timezone is always the one the External_Scheduler interprets wall-clock times in, never the
 * browser's (Requirement 5.6): a user in another timezone reading their own local time here would
 * calculate the wrong answer. Falls back to UTC, labelled, if the deployment timezone is not one the
 * runtime recognises, rather than silently falling back to local time.
 *
 * Returns null for a missing or unparseable timestamp, since the reserved tag it derives from is
 * advisory and may be absent or malformed.
 *
 * @param {string} projectedStopAt - ISO 8601 timestamp from the SchedulingInfo response
 * @param {string} timezone - `schedulerTimezone` from the SchedulingInfo response
 * @returns {string|null}
 */
function formatProjectedStop(projectedStopAt, timezone) {
  if (typeof projectedStopAt !== 'string' || projectedStopAt.trim().length === 0) return null;

  const stopAt = new Date(projectedStopAt);
  if (Number.isNaN(stopAt.getTime())) return null;

  const options = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  try {
    return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: timezone }).format(stopAt);
  } catch (error) {
    // An unrecognised timezone identifier throws a RangeError. UTC is wrong but honest; local time
    // would be wrong and would look right, which is worse.
    return `${new Intl.DateTimeFormat('en-AU', { ...options, timeZone: 'UTC' }).format(stopAt)} UTC`;
  }
}

/**
 * Renders the projected stop time with the timezone it is in.
 *
 * `formatProjectedStop` deliberately leaves the zone off, because the modal names it once in its own
 * badge. The cards and the list have no such badge and a bare time there would be read as local, so
 * the zone travels with the time. The UTC fallback already names itself, so it is left alone.
 *
 * @param {string} projectedStopAt - ISO 8601 timestamp
 * @param {string} timezone - the deployment timezone
 * @returns {string|null} Null whenever `formatProjectedStop` has nothing to render
 */
function formatProjectedStopWithZone(projectedStopAt, timezone) {
  const formatted = formatProjectedStop(projectedStopAt, timezone);
  if (formatted === null) return null;

  const zone = typeof timezone === 'string' ? timezone.trim() : '';
  if (zone.length === 0 || formatted.endsWith('UTC')) return formatted;

  return `${formatted} (${zone})`;
}

/**
 * Projects when an idle workspace will be stopped, from the warning timestamp and the timeout.
 *
 * The same arithmetic the backend performs for `projectedStopAt` (Requirement 4.8), kept here so that
 * the workspace list, which has no SchedulingInfo response to read that field from, computes it the
 * one way rather than inlining a second copy. The detail page prefers the server's value; this is what
 * the list uses and what the detail page falls back to.
 *
 * Returns null rather than a guess whenever any input is unusable: no warning is outstanding, no
 * timeout to project from, or a multiplier that is not a real number. Callers say "soon" in that case.
 * A multiplier of zero is honoured and yields the warning time itself, since a deployment configured
 * with no grace period stops at the moment it warns.
 *
 * @param {string} warnedAt - The `IdleStopWarnedAt` timestamp, ISO 8601
 * @param {number} minutes - The idle timeout in minutes
 * @param {number} graceMultiplier - `schedulerIdleGraceMultiplier`
 * @returns {string|null} An ISO 8601 timestamp
 */
function projectedStopFrom(warnedAt, minutes, graceMultiplier) {
  if (typeof warnedAt !== 'string' || warnedAt.trim().length === 0) return null;

  const warnedAtMs = new Date(warnedAt).getTime();
  if (Number.isNaN(warnedAtMs)) return null;

  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  if (!Number.isFinite(graceMultiplier) || graceMultiplier < 0) return null;

  return new Date(warnedAtMs + minutes * graceMultiplier * 60 * 1000).toISOString();
}

/**
 * The one-line schedule phrase for a workspace card, from the mirrored Schedule_Tag value alone.
 *
 * The mirror does not hold the groups table, so a preset can only be named as the tag names it; the
 * detail page, which does make the call, names it as the administrator described it. A custom schedule
 * is fully self-describing from the tag and is collapsed by `describeSavedSchedule`, which is reused
 * here so the list and the modal phrase the same timetable the same way.
 *
 * The timetable alone, with no `Scheduled:` prefix: the card carries the word in the label of the pill
 * this phrase is the tooltip of, and a tooltip that restated its own trigger would say nothing. The
 * compact rendering, which shows an icon and no label, prefixes the word itself.
 *
 * Returns null when nothing is scheduled, which is the signal to render no marker at all rather than
 * one saying nothing is set. A card is not a settings page: an absent fact is left absent.
 *
 * @param {string} scheduleValue - The mirrored Schedule_Tag value
 * @returns {string|null}
 */
function describeMirroredSchedule(scheduleValue) {
  const phrase = describeSavedSchedule(parseScheduleValue(scheduleValue));

  return phrase === NO_SCHEDULE_PHRASE ? null : phrase;
}

/**
 * The one-line idle-stop phrase for a workspace card, from the mirrored Idle_Tag value alone.
 *
 * Null when no timeout is set, for the same reason as above.
 *
 * @param {number} idleStopMinutes - The mirrored Idle_Tag value in minutes
 * @returns {string|null}
 */
function describeMirroredIdleStop(idleStopMinutes) {
  if (!Number.isFinite(idleStopMinutes) || idleStopMinutes <= 0) return null;

  return `Stops after ${formatDuration(idleStopMinutes)} idle`;
}

/**
 * The phrase naming an idle stop the scheduler already performed, for a card that renders the relative
 * time beside it.
 *
 * Past tense and no timeout in it: this says what happened to the workspace, not what is configured on
 * it. The two are different facts, and a stopped workspace's future timeout is not the one the user is
 * asking about when they find it stopped.
 *
 * The phrase alone, with no time: the caller renders the relative time as a `TimeAgo`, the same way the
 * created-at fact beside it is rendered, so the phrasing lives here and the rendering stays in the card.
 *
 * Returns null when the timestamp is absent or unreadable, which is the signal to render nothing. The
 * tag is advisory and may hold anything, and a card claiming a stop it cannot date is worse than silence.
 *
 * @param {string} stoppedAt - The `IdleStopStoppedAt` timestamp, plain ISO 8601
 * @returns {string|null}
 */
function describeIdleStopEvent(stoppedAt) {
  if (typeof stoppedAt !== 'string' || stoppedAt.trim().length === 0) return null;
  if (Number.isNaN(new Date(stoppedAt.trim()).getTime())) return null;

  return 'Stopped for being idle';
}

export {
  summariseWindows,
  formatSummaryLine,
  displayGroupName,
  describeSavedSchedule,
  describeMirroredSchedule,
  describeMirroredIdleStop,
  describeIdleStopEvent,
  projectedStopFrom,
  presetOptionLabel,
  presetOptionTitle,
  sortPresetGroups,
  timeOptions,
  idleStopOptions,
  idleStopGroup,
  formatProjectedStop,
  formatProjectedStopWithZone,
  minutesToTime,
  formatDuration,
  formatDayList,
  formatGroupWindow,
  DAY_LABELS,
  SHORT_DAY_LABELS,
  IDLE_STOP_LOCKED_NOTICE,
};
